import React from 'react';
import PropTypes from 'prop-types';
import { Flex, Grid, P, TextField, Toggle } from '@decisiv/ui-components';
import { t, Trans } from '@lingui/macro';
import { FormGroup } from './StyledComponents';
import FeatureInfoDrawer from './FeatureInfoDrawer';
import { secondsToHumanReadable } from '../../utils/dates';

const AutomaticRetrySettings = ({
  edit,
  automaticRetryEnabled,
  retryAfter,
  maxRetries,
  warningMessageMaxRetryAfterMessage,
  handleTextChange,
  setAutomaticRetryEnabled,
}) => (
  <>
    <Grid.Column span="2">
      <FeatureInfoDrawer
        heading={<Trans>Automatic Retries</Trans>}
        lead={<Trans>Enable/Disable Automatic Retries</Trans>}
        ariaLabel={t`More information about Automatic Retries`}
      >
        <p>
          <Trans>
            Available for application events integrations only. HTTP
            integrations don’t support automatic retries.
          </Trans>
        </p>
        <p>
          <Trans>
            When enabled, requests that fail with a 429 (Too Many Requests) or a
            5xx server error are retried automatically instead of failing
            outright.
          </Trans>
        </p>
        <ul>
          <li>
            <Trans>
              Each retry waits for the configured retry-after period before
              trying again, giving the partner system time to recover.
            </Trans>
          </li>
          <li>
            <Trans>
              Retries continue until the request succeeds or the maximum retry
              count is reached, whichever comes first.
            </Trans>
          </li>
          <li>
            <Trans>
              If the maximum retry count is reached without success, the request
              is treated as failed and handled the same way as any other failed
              request.
            </Trans>
          </li>
        </ul>
        <p>
          <Trans>
            Exhausted retries count toward the Circuit Breaker’s failure
            threshold on every attempt. When retries are exhausted, the request
            ends in a plain FAILED state. The “queued on circuit-open” behavior
            is a separate path that only applies when Automatic Retry is
            disabled.
          </Trans>
        </p>
      </FeatureInfoDrawer>
    </Grid.Column>
    <Grid.Column>
      {edit ? (
        <FormGroup key="cache">
          {automaticRetryEnabled ? (
            <Grid.Column>
              <span className="spanDetail">
                <P>Enabled</P>
              </span>
              <span>
                <P>Yes</P>
              </span>
              <span className="spanDetail">
                <P>Retry After</P>
              </span>
              <span>
                <P>
                  {retryAfter} seconds ({secondsToHumanReadable(retryAfter)})
                </P>
              </span>
              <span className="spanDetail">
                <P>Max Retries</P>
              </span>
              <span>
                <P>{maxRetries}</P>
              </span>
            </Grid.Column>
          ) : (
            <Grid.Column>
              <span className="spanDetail">
                <P>Enabled</P>
              </span>
              <span>
                <P>No</P>
              </span>
            </Grid.Column>
          )}
        </FormGroup>
      ) : (
        <FormGroup key="automaticRetry">
          <Toggle
            name="toggle_automatic_retry"
            options={[
              {
                id: 'disabled',
                text: t`Disabled`,
              },
              { id: 'enabled', text: t`Enabled` },
            ]}
            onChange={(e) => {
              setAutomaticRetryEnabled(e === 'enabled');
            }}
            selected={automaticRetryEnabled ? 'enabled' : 'disabled'}
          />
          {automaticRetryEnabled ? (
            <FormGroup>
              <TextField
                label={t`Automatic Retry After in seconds(Max 900)`}
                name="retry_after"
                required
                type="number"
                value={String(retryAfter)}
                onChange={handleTextChange}
              />
              <Flex marginTop={1}>
                <TextField
                  label={t`Max Retries`}
                  name="max_retries"
                  required
                  type="number"
                  value={String(maxRetries)}
                  warningMessage={warningMessageMaxRetryAfterMessage}
                  onChange={handleTextChange}
                />
              </Flex>
            </FormGroup>
          ) : null}
        </FormGroup>
      )}
    </Grid.Column>
  </>
);

AutomaticRetrySettings.propTypes = {
  edit: PropTypes.bool.isRequired,
  automaticRetryEnabled: PropTypes.bool.isRequired,
  retryAfter: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  maxRetries: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  warningMessageMaxRetryAfterMessage: PropTypes.string.isRequired,
  handleTextChange: PropTypes.func.isRequired,
  setAutomaticRetryEnabled: PropTypes.func.isRequired,
};

export default AutomaticRetrySettings;
